/**
 * CSV utilities — one canonical implementation so every feature stops rolling
 * its own escape logic, file download trick, or clipboard fallback.
 *
 * Pattern:
 *
 * ```ts
 * const csv = toCSV(invoices, [
 *   { header: 'Invoice #', value: (i) => i.invoiceNumber },
 *   { header: 'Total',     value: (i) => i.total },
 * ]);
 * downloadCSV('invoices.csv', csv);
 * await copyCSVToClipboard(csv);
 * ```
 */

export interface CSVColumn<T> {
  header: string;
  value: (row: T) => string | number | boolean | null | undefined | Date;
}

const NEEDS_QUOTING = /[",\n\r]/;

// CSV/formula injection (OWASP): a cell whose text begins with = + - @ (or a
// leading tab / carriage return) is evaluated as a formula by Excel, Sheets, and
// LibreOffice when the file is opened. Neutralize it by prefixing a single quote.
// Applied to STRING values only — number/Date/boolean cells (e.g. a negative
// amount like -81.12) are produced by us, never user-controlled, and must pass
// through untouched so exported figures stay numeric.
const FORMULA_TRIGGER = /^[=+\-@\t\r]/;

function formatCell(value: unknown): string {
  if (value === null || value === undefined) return '';
  if (value instanceof Date) return value.toISOString();
  const isUserText = typeof value === 'string';
  let s = String(value);
  if (isUserText && FORMULA_TRIGGER.test(s)) {
    s = `'${s}`;
  }
  if (NEEDS_QUOTING.test(s)) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
}

/**
 * Serialize rows to RFC-4180-ish CSV. Header row first, CRLF line endings.
 */
export function toCSV<T>(rows: T[], columns: CSVColumn<T>[]): string {
  const lines: string[] = [];
  lines.push(columns.map((c) => formatCell(c.header)).join(','));
  for (const row of rows) {
    lines.push(columns.map((c) => formatCell(c.value(row))).join(','));
  }
  return lines.join('\r\n');
}

/**
 * Trigger a file download for `csv` named `filename`. Adds the UTF-8 BOM so
 * Excel opens accents correctly.
 */
export function downloadCSV(filename: string, csv: string): void {
  const BOM = '\uFEFF';
  const blob = new Blob([BOM + csv], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename.endsWith('.csv') ? filename : `${filename}.csv`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  // Defer revoke to next tick so Safari has time to start the download.
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

/**
 * Copy `csv` to the system clipboard. Uses the async Clipboard API where
 * available; falls back to a hidden textarea + `execCommand` for older browsers
 * and non-secure contexts.
 */
export async function copyCSVToClipboard(csv: string): Promise<void> {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(csv);
    return;
  }
  const textarea = document.createElement('textarea');
  textarea.value = csv;
  textarea.style.position = 'fixed';
  textarea.style.opacity = '0';
  document.body.appendChild(textarea);
  textarea.select();
  try {
    document.execCommand('copy');
  } finally {
    textarea.remove();
  }
}
